/**
 * Money handling.
 *
 * Amounts arrive from imports and from forms as decimal dollars, and often as strings
 * (`{"quantity":"1","unitPrice":"250","total":"287.44"}`). Floats cannot represent those
 * values exactly, and rounding drift on a customer's invoice is not acceptable, so money is
 * carried internally as a scaled integer of cents and rendered from that.
 *
 * Postgres stores `numeric(14,2)`; this module converts at the boundary.
 */

export type Cents = number & { readonly __brand: 'Cents' };

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/**
 * Parse an imported or user-entered amount into cents.
 *
 * Accepts `250`, `"250"`, `"287.44"`, `"1 234,56"` (French formatting), `null`.
 * Returns 0 for null/undefined/blank so callers do not each re-handle absent money.
 */
export function toCents(value: unknown): Cents {
  if (value === null || value === undefined || value === '') return 0 as Cents;
  if (isFiniteNumber(value)) return Math.round(value * 100) as Cents;
  if (typeof value !== 'string') return 0 as Cents;

  // French input may use a comma decimal separator and non-breaking-space grouping.
  const cleaned = value
    .replace(/ | |\s/g, '')
    .replace(/(\d),(\d{1,2})$/, '$1.$2')
    .replace(/,/g, '');

  const n = Number.parseFloat(cleaned);
  return Number.isFinite(n) ? (Math.round(n * 100) as Cents) : (0 as Cents);
}

/** Cents to the `numeric(14,2)` string Postgres wants. Never a float. */
export function centsToNumeric(cents: Cents | number): string {
  const c = Math.round(cents);
  const sign = c < 0 ? '-' : '';
  const abs = Math.abs(c);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

export const addCents = (...values: Array<Cents | number>): Cents =>
  values.reduce<number>((sum, v) => sum + Math.round(v), 0) as Cents;

/**
 * Multiply money by a quantity. Quantities can be fractional (hours, metres), so the
 * result is rounded once, at the end, to avoid accumulating half-cents.
 */
export const multiplyCents = (cents: Cents | number, quantity: number): Cents =>
  Math.round(cents * quantity) as Cents;

/** Apply a percentage rate (e.g. tax at 9.975) to an amount. */
export const percentOf = (cents: Cents | number, ratePercent: number | string): Cents => {
  const rate = typeof ratePercent === 'string' ? Number.parseFloat(ratePercent) : ratePercent;
  if (!Number.isFinite(rate)) return 0 as Cents;
  return Math.round((cents * rate) / 100) as Cents;
};

/**
 * Format for display. Quebec French convention is `1 234,56 $` with a non-breaking space
 * before the sign; English Canadian is `$1,234.56`. Intl handles both given the locale.
 */
export function formatMoney(cents: Cents | number, locale: 'fr' | 'en' = 'fr', currency = 'CAD'): string {
  return new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

/** Compact form for dense grids and cards: `1,2 k$` / `$1.2k`. */
export function formatMoneyCompact(cents: Cents | number, locale: 'fr' | 'en' = 'fr', currency = 'CAD'): string {
  return new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(cents / 100);
}

/**
 * Margin from sell and cost. Line items carry `unitCostPrice` and `unitPrice`, so job
 * profitability is computable.
 * Returns null rather than 0 when there is no revenue, so "no data" and "zero margin"
 * stay distinguishable.
 */
export function marginPercent(revenueCents: Cents | number, costCents: Cents | number): number | null {
  if (!revenueCents) return null;
  return ((revenueCents - costCents) / revenueCents) * 100;
}
